import { HeroUnsubscribe } from 'components/Unsubscribe';
import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { HeadMeta } from 'components/common/HeadMeta';
import { HeaderNav } from 'components/common/HeaderNav';
import { Footer } from 'components/blocks/Footer';
import { useRouter } from 'next/router';
import IntegrityError from 'page-components/400';
import React, { useEffect } from 'react';
import { GetStaticProps, GetStaticPropsResult } from 'next';
import {
  fetchBackgroundImages,
} from 'api/home/static';
import {
  IBackgroundImages,
} from 'api/home/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { locale } from 'i18n/constants';
import { AnalyticsContext } from 'util/analytics';
import { Diagnostics } from 'util/diagnostics';
import { useQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import { HeaderNavCurLink } from 'components/common/HeaderNav/MobileSummerHeader';

export type Props = {
  backgroundImage: IBackgroundImages;
};

export const DEBUG_TAG = 'unsubscribe-page';
export const TEMPLATE_ID = '9.9';
export const TEMPLATE_NAME = 'Unsubscribe Page';

export default function UnsubscribePage({
  backgroundImage,
}: Props) {
  const { t } = useOTSTranslation('common');
  const route = useRouter();
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/naming-convention
    email, type, subid, utm_campaign, utm_medium, utm_source, langkey,
  } = route.query;

  const isValid = !!email && !!subid;

  useEffect(() => {
    if (!isValid) {
      Diagnostics.message({ message: 'Not a valid unsubscribe page' });
    }
  }, []);

  const {
    current: curBackgroundImage, hasFreshData: isImageFresh,
  } = useQueryWithInitialValue(['skireport-fetchBackgroundImages'],
    async () => fetchBackgroundImages(),
    backgroundImage);

  if (!isValid) {
    return <IntegrityError />;
  }

  return (
    <AnalyticsContext templateId={TEMPLATE_ID} templateName={TEMPLATE_NAME}>
      <AdUnitContext
        adType="other"
        adTKey="unsubscibe"
      >
        <div style={{ background: '#f4f5f5' }}>
          <HeadMeta
            title={t('email.unsubscribe.message')}
          />
          <HeaderNav current={HeaderNavCurLink.MAIN} />
          <HeroUnsubscribe
            email={email as string}
            subid={subid as string}
            loading={!isImageFresh}
            backgroundImage={curBackgroundImage}
          />
          <Footer />
        </div>
      </AdUnitContext>
    </AnalyticsContext>
  );
}

// This function gets called at build time
export const getStaticProps: GetStaticProps<Props> = async () => {
  const backgroundImage = await fetchBackgroundImages();

  const f: GetStaticPropsResult<Props> = {
    props: {
      backgroundImage,
      ...(await serverSideTranslations(locale, ['common', 'unit', 'seo'])),
    },
  };
  return f;
};
